import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Modal,
  Button,
  Input,
  Upload,
  Table,
  Typography,
  Space,
  message,
  Popconfirm,
  Divider,
  AutoComplete,
  Spin
} from 'antd';
import {
  literatureService,
  type UserCslStyleInfo
} from '../../services/literature/literatureService';
import '../../styles/literature-stitch.css';

export interface UserCslStyleModalProps {
  open: boolean;
  onClose: () => void;
  /** 上传或删除成功后回调，用于父组件刷新导出菜单 */
  onStylesChanged?: () => void;
}

const UserCslStyleModal: React.FC<UserCslStyleModalProps> = ({ open, onClose, onStylesChanged }) => {
  const [list, setList] = useState<UserCslStyleInfo[]>([]);
  const [cslUploadLabel, setCslUploadLabel] = useState('');
  const [cslUploading, setCslUploading] = useState(false);

  const [catalogOptions, setCatalogOptions] = useState<{ value: string; label: React.ReactNode }[]>([]);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [catalogImporting, setCatalogImporting] = useState(false);
  const [catalogSource, setCatalogSource] = useState('');
  const [catalogTruncated, setCatalogTruncated] = useState(false);
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const load = useCallback(async () => {
    try {
      const rows = await literatureService.listUserCslStyles();
      setList(Array.isArray(rows) ? rows : []);
    } catch {
      setList([]);
    }
  }, []);

  const fetchCatalog = useCallback(async (q: string) => {
    setCatalogLoading(true);
    try {
      const res = await literatureService.searchCslCatalog(q, 40, 0);
      setCatalogSource(res.source || '');
      setCatalogTruncated(!!res.truncated);
      setCatalogOptions(
        res.items.map((it) => ({
          value: it.path,
          label: (
            <span style={{ whiteSpace: 'normal', wordBreak: 'break-all' }}>
              <strong>{it.label}</strong>
              <span className="lit-csl-catalog-path">{it.path}</span>
            </span>
          )
        }))
      );
    } catch {
      setCatalogOptions([]);
    } finally {
      setCatalogLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    void load();
    void fetchCatalog('');
  }, [open, load, fetchCatalog]);

  useEffect(() => {
    return () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    };
  }, []);

  const onCatalogSearch = (text: string) => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      void fetchCatalog(text);
    }, 320);
  };

  return (
    <Modal
      rootClassName="lit-stitch-modal lit-csl-style-modal"
      title="自定义 CSL 引用样式"
      open={open}
      onCancel={onClose}
      footer={[
        <Button key="close" type="default" className="lit-stitch-btn-secondary" onClick={onClose}>
          关闭
        </Button>
      ]}
      width={760}
      destroyOnHidden
    >
      <Typography.Paragraph type="secondary" style={{ fontSize: 13, marginBottom: 12 }}>
        搜索列表优先使用服务器本地的{' '}
        <a href="https://github.com/citation-style-language/styles" target="_blank" rel="noopener noreferrer">
          官方 styles 仓库
        </a>
        全量镜像（约每 3 个月自动更新）；可在下方搜索并一键加入「我的样式」，也可上传本机 .csl。加入后可在「导出引用」的「已上传的 CSL」中选用；复制到 Word 时可保留斜体等格式。
      </Typography.Paragraph>

      <Typography.Title level={5} className="lit-csl-section-title" style={{ marginTop: 0 }}>
        从官方库搜索并添加
      </Typography.Title>
      {catalogSource === 'bundle' && (
        <Typography.Paragraph type="success" style={{ fontSize: 12, marginBottom: 8 }}>
          当前使用服务器本地的官方样式全量包（每季度自动从 GitHub 更新）；搜索与导入不依赖外网。
        </Typography.Paragraph>
      )}
      {catalogSource === 'seed' && (
        <Typography.Paragraph type="warning" style={{ fontSize: 12, marginBottom: 8 }}>
          当前无本地样式包且无法从 GitHub 拉取目录，已使用内置短列表。可在服务器执行{' '}
          <Typography.Text code>npm run sync:csl-bundle</Typography.Text> 下载全量包。
        </Typography.Paragraph>
      )}
      {catalogTruncated && catalogSource === 'github' && (
        <Typography.Paragraph type="warning" style={{ fontSize: 12, marginBottom: 8 }}>
          GitHub 返回的目录可能被截断，若搜不到某样式请稍后再试或从仓库网页确认路径后本地上传。
        </Typography.Paragraph>
      )}
      <Space direction="vertical" style={{ width: '100%' }} size="small">
        <AutoComplete
          style={{ width: '100%' }}
          options={catalogOptions}
          onSearch={onCatalogSearch}
          onSelect={(path) => {
            void (async () => {
              setCatalogImporting(true);
              try {
                const r = await literatureService.importCslFromCatalog(String(path));
                message.success(`已添加：${r.label}`);
                await load();
                onStylesChanged?.();
              } catch (e: any) {
                message.error(e.message || '导入失败');
              } finally {
                setCatalogImporting(false);
              }
            })();
          }}
          notFoundContent={catalogLoading ? <Spin size="small" /> : '无匹配项'}
          placeholder="输入关键词，如 angew、nature、ieee、chicago…（约 40 条预览，清空可恢复默认列表）"
          allowClear
          disabled={catalogImporting}
        />
        <div className="lit-csl-muted">
          共检索到条目由服务端缓存；依赖型（dependent）短文件可能无法通过校验，请选仓库根目录下的独立 .csl 或本地上传。
        </div>
      </Space>

      <Divider style={{ margin: '16px 0' }}>或上传本机文件</Divider>

      <Space direction="vertical" style={{ width: '100%' }} size="middle">
        <div>
          <div style={{ marginBottom: 6, fontSize: 13 }}>显示名称（可选）</div>
          <Input
            value={cslUploadLabel}
            onChange={(e) => setCslUploadLabel(e.target.value)}
            placeholder="不填则使用文件名"
            allowClear
          />
        </div>
        <Upload
          accept=".csl,application/xml,text/xml"
          maxCount={1}
          showUploadList
          beforeUpload={async (file) => {
            setCslUploading(true);
            try {
              const res = await literatureService.uploadUserCslStyle(
                file,
                cslUploadLabel.trim() || undefined
              );
              message.success(`已保存：${res.label}`);
              setCslUploadLabel('');
              await load();
              onStylesChanged?.();
            } catch (e: any) {
              message.error(e.message || '上传失败');
            } finally {
              setCslUploading(false);
            }
            return false;
          }}
        >
          <Button type="primary" className="lit-stitch-btn-primary" loading={cslUploading}>
            选择 .csl 文件上传
          </Button>
        </Upload>
        <Table<UserCslStyleInfo>
          size="small"
          rowKey="styleKey"
          dataSource={list}
          pagination={false}
          locale={{ emptyText: '暂无自定义样式' }}
          columns={[
            { title: '名称', dataIndex: 'label', key: 'label', ellipsis: true },
            { title: 'styleKey', dataIndex: 'styleKey', key: 'sk', ellipsis: true, width: 200 },
            {
              title: '操作',
              key: 'op',
              width: 88,
              render: (_, r) => (
                <Popconfirm
                  title="确定删除该样式？"
                  onConfirm={async () => {
                    try {
                      await literatureService.deleteUserCslStyle(r.styleKey);
                      message.success('已删除');
                      await load();
                      onStylesChanged?.();
                    } catch (e: any) {
                      message.error(e.message || '删除失败');
                    }
                  }}
                >
                  <Button type="default" danger size="small" className="lit-stitch-btn-secondary">
                    删除
                  </Button>
                </Popconfirm>
              )
            }
          ]}
        />
      </Space>
    </Modal>
  );
};

export default UserCslStyleModal;
